import storedValue from './storedValue'

export default {
  user_list: {
    page_title: 'Customers | Thanx',
    title: 'All customers',
    total_users: 'Total customers in your database',
    total_users_popover:
      'Customers that have not opted in to share their PII (Personally Identifiable Information) may not appear in the search below.',
    import_customers: 'Import customers',
    import_email_subscribers: 'Import email subscribers',
    search_placeholder: 'Search customers by email, ID, or phone',
    table: {
      name: 'Name',
      view_profile: 'View profile',
    },
    tabs: {
      all_customers: {
        title: 'All customers',
      },
      top_50_customers: {
        title: 'Top 50 customers',
      },
    },
  },
  view: {
    blocked: 'BLOCKED',
    grant_reward: 'Grant reward',
    back: 'Back',
    title: 'User Name',
    member_since: 'Member since %{date}',
    tier: 'TIER',
    tier_restricted: 'Tier details require additional permissions',
    counters: {
      lifetime_spend: 'Lifetime spend',
      lifetime_purchases: 'Lifetime purchases',
      average_check: 'Avg. check',
      points_balance: 'Current points balance',
    },
    activity: {
      page_title: 'Activity | Thanx',
      title: 'Activity',
      panels: {
        overview: 'Overview',
        orders: 'Digital orders',
        items_purchased: 'Items purchased',
        rewards: 'Rewards',
        points_expiration: 'Points expiration',
        // FR-29 (AI-1288): renamed from 'Feedback' so it matches the rest of
        // the RecoveryAI Order feedback blueprint. Panel KEY stays 'feedback'
        // (an internal id, not user-visible) — only this label changes, and no
        // sibling panel entry is touched.
        feedback: 'Order feedback',
        campaigns: 'Emails',
      },
      overview: {
        title: 'Overview',
        empty_state: 'There is no activity for this customer',
        table: {
          date: 'Date',
          activity: 'Activity',
          location: 'Location',
          order_details: 'Order details',
          manually_added: 'Manually added via the Thanx dashboard',
          // AI-1289 (FR-32): action-column link on an order_rating_submitted
          // row, per R4 S5 ("View feedback"). Routes to Order feedback, not a
          // modal, so it's a separate string from order_details above.
          view_feedback: 'View feedback',
        },
      },
      orders: {
        title: 'Digital orders',
        id: 'Thanx ID',
        provider_order_uid: 'Order ID from ordering provider',
        provider_basket_uid: 'Basket ID from ordering provider',
        handoff: 'Handoff mode',
        status: 'Status',
        total: 'Total',
        placed_at: 'Placed at',
        // AI-1290 (FR-33): Rating column, left of Details. `resolved_by` is
        // ONLY used for the non-AI (operator) branch — the AI branch reuses
        // FeedbackResolvedByCell's own badge copy, not this string.
        rating: 'Rating',
        resolved_by: 'Resolved by %{name}',
        details: 'Details',
        detail: {
          order: 'Order Details',
          title: 'Digital order',
          okay: 'Okay',
          status: 'Status',
          handoff: 'Handoff',
          provider_order_uid: 'Order ID from ordering provider',
          provider_basket_uid: 'Basket ID from ordering provider',
          location: 'Location',
          placed_at: 'Placed At',
          gift_card_payments: 'Gift Card Payments',
          gift_card_states: {
            pending: 'Pending',
            processed: 'Charged',
            refunded: 'Refunded',
          },
          amounts: 'Amounts',
          total: 'Total',
          subtotal: 'Subtotal',
          discount: 'Discount',
          tip: 'Tip',
          tax: 'Tax',
        },
        status_badge: {
          pending: 'Pending',
          placed: 'Placed',
          billed: 'Billed',
          completed: 'Completed',
          canceled: 'Canceled',
          refunded: 'Refunded',
          archived: 'Archived',
        },
        empty_state: 'There are no digital orders for this customer',
        truncated:
          'Showing the %{count} most recent order. This customer has older orders that are not listed here.',
        truncated_plural:
          'Showing the %{count} most recent orders. This customer has older orders that are not listed here.',
      },
      order_details: {
        order: 'Order',
        purchase: 'Purchase',
        provider: 'Provider',
        location: 'Location',
        date_and_time: 'Date and time',
        order_details: 'Order details',
        subtotal: 'Sub-total',
        tax: 'Tax',
        total: 'Total',
        item: 'Item',
        price: 'Price',
        items_in_this_order: 'Items in this order',
        okay: 'Okay',
        external_order_id: 'External Order ID',
        no_pos_data: 'No matching POS data available',
        card_processed_for: 'Card processed for',
        receipt: {
          heading: 'Itemized order',
          caption_line_totals:
            'Items in this order, with quantities and line totals',
          item: 'Item',
          quantity: 'Qty',
          line_total: 'Line total',
          item_price_unavailable: 'Not available',
          modifier_included_above: 'Included above',
          modifier_of: 'Modifier of %{item}',
          order_id: 'POS order ID',
          status: 'Status',
          channel: 'Channel',
          statuses: {
            placed: 'Placed',
            voided: 'Voided',
            refunded: 'Refunded',
          },
          channels: {
            digital: 'Online',
            instore: 'In-store',
            third_party: '3rd party',
          },
          totals: {
            subtotal: 'Sub-total',
            tax: 'Tax',
            tip: 'Tip',
            service_charge: 'Service charge',
            discount: 'Discount',
            total: 'Order total',
          },
          totals_notes: {
            adjustments:
              'Whether the sub-total already reflects a discount or service charge varies by POS provider.',
            amount_charged:
              'Card processed for may differ because of a separate tender or a later payment adjustment.',
          },
        },
      },
      rewards: {
        title: 'Rewards',
        reward: 'Reward',
        delivered_at: 'Delivered',
        redeemed_at: 'Redeemed',
        status: 'Status',
        state: {
          available: 'DELIVERED',
          active: 'ACTIVE',
          in_progress: 'IN PROGRESS',
          retired: 'RETIRED',
          used: 'USED',
          fraudulent: 'FRAUDULENT',
        },
        empty_state: 'There are no rewards for this customer',
        issuance_description: {
          granted: 'Granted reward',
          intro: 'Intro reward',
        },
        actions: {
          retire: 'Retire',
          reissue: 'Reissue',
        },
        action_modal: {
          retire: {
            title: 'Retire %{name}',
            proceed: 'Retire this reward',
            success: 'The reward has been retired',
            failure: 'Error retiring the reward',
            description:
              'This customer will no longer have access to this reward. If you change your mind you will need to grant a new reward to this customer.',
          },
          reissue: {
            title: 'Reissue %{name}',
            proceed: 'Reissue this reward',
            success:
              'The reward is being reissued. The user will receive it in a few minutes',
            failure: 'Error reissuing the reward',
            description: 'Grant this reward again to this customer.',
          },
        },
      },
      points_expiration: {
        title: 'Points expiration',
        empty_state: 'This customer has no points',
        table: {
          amount: 'Amount',
          expiration_date: 'Expiration date',
          status: 'Status',
          actions: 'Actions',
        },
        status: {
          expired: 'Expired',
          scheduled: 'Scheduled',
          reversed: 'Reversed',
        },
        actions: {
          reverse: 'Reverse',
        },
        reverse_modal: {
          title: 'Reverse expiration?',
          description:
            "Are you sure you want to reverse this expired transaction? This will return %{amount} points to the customer's balance using today's date.",
          confirm: 'Reverse points',
          success: 'Points successfully reversed',
          error: 'Failed to reverse points',
        },
      },
      // Legacy NPS-shaped UserFeedback panel (getUserFeedbacks / users/:id/feedbacks).
      // AI-1288 repoints the 'feedback' panel slot at order_feedback below, which
      // makes this stack unreachable from the UI — deliberately left in place
      // (not deleted) since /users/:identifier/feedbacks is still a live
      // endpoint a later PR must gate. Do not remove without confirming that
      // gate first.
      feedback: {
        title: 'Feedback',
        empty_state: 'There is no feedback from this customer',
        date: 'Date',
        rating: 'Rating',
        comment: 'Comment',
        location: 'Location',
        order_details: 'Order details',
      },
      // AI-1288 (FR-29/30/31): RecoveryAI order-feedback history, reusing the
      // shared components/FeedbackTable that also backs the Order feedback
      // inbox (Reports > Order feedback > Details).
      order_feedback: {
        title: 'Order feedback',
        // S2 (RecoveryAI Customer Profile Page design spec), verbatim.
        subtitle:
          'Comments this customer has left on past orders after providing a negative rating.',
        empty_state:
          'This customer has not written feedback for any of their orders.',
        // hasActiveFilters is always false on this surface (no filter UI, see
        // FeedbackTable's filteredEmptyMessage prop) — filtered_empty_state is
        // therefore unreachable in practice; it exists only to satisfy the
        // shared table's required prop, and mirrors empty_state so an
        // unreachable code path can never show different copy than the state
        // it can't distinguish itself from.
        filtered_empty_state:
          'This customer has not written feedback for any of their orders.',
        error: 'Could not load this customer’s order feedback.',
        load_more: 'Load more',
        // AI-1291 (FR-61): shown in place of the table for an operator who
        // lacks the Ratings & Reviews (Feedback) view permission. `%{label}`
        // mirrors orderRatings.ts's overview_forbidden pattern — resolved
        // live via usePermissionResourceLabel, falling back to the seeded
        // display_name (nucleus/lib/rbac_seeder.rb) when that hasn't loaded.
        forbidden: {
          title: "You don't have permission to view this",
          message:
            'Order feedback requires "%{label}" access. Contact your admin to update your permission settings.',
          permission_label_fallback: 'Ratings & Reviews',
        },
      },
      campaigns: {
        title: 'Emails',
        empty_state:
          'There are no emails for this customer. (This data updates every hour)',
        email: 'Email',
        delivered: 'Delivered',
        engagement: 'Engagement',
        report_link: 'Report',
        not_live: 'This data updates every hour',
        engagement_types: {
          bounced: 'bounced',
          clicked: 'clicked',
          delivered: 'delivered',
          marked_as_spam: 'unsubscribed',
          opened: 'opened',
          sent: 'sent',
          server_rejected: 'rejected',
        },
      },
      purchased_items: {
        title: 'Items purchased',
        details: 'Details',
        item: 'Item',
        number_of_times: 'Number of times purchased',
        okay: 'Okay',
        empty_state: 'There are no purchases for this customer',
        purchased_times: 'Purchased %{count} times by %{by}',
        purchases: {
          date: 'Date',
          order_number: 'Order number',
          order_details: 'Order details',
        },
      },
    },
    profile: {
      page_title: 'Profile | Thanx',
      title: 'Profile',
      panels: {
        overview: 'Overview',
        custom_tags: 'Custom attributes',
        transferred_points: 'Transferred Points',
      },
      overview: {
        title: 'Overview',
        contact: 'Contact details',
        profile: 'Profile details',
        notification: 'Notification details',
        email: 'Email',
        phone: 'Phone',
        birthday: 'Birthday',
        user_lists: 'User Lists',
        import_files: 'Import files',
        marketing_campaigns: 'Marketing campaigns',
        transactional_via_push:
          'Transactional notifications via push (available if the user has the app installed)',
        transaction_via_email:
          'Transactional notifications via email (available if the user has the app installed)',
      },
      custom_tags: {
        title: 'Custom attributes',
        empty_state: 'There are no custom attributes for this customer',
        key: 'Attribute',
        values: 'Value',
        month: 'Month',
        day: 'Day',
        whos_celebrating: "Who's celebrating",
      },
      pii_banner: {
        pii_not_shared:
          'This customer has not opted in to sharing their PII (Personally Identifiable Information). Their last name, email, phone number, birthday and some custom data tags are not available.',
      },
      transferred_points: {
        title: 'Transferred points',
        points_transferred:
          'This customer’s points from your previous loyalty program have been transferred to points in your loyalty program on Thanx. ',
        description:
          'Customers automatically receive their transferred points when they sign up or log in to your app or web experience for the first time. Customers must use the email or phone number included in the transfer file that your provided to the Thanx team. The number of points customers receive is also determined by what you specified in the transfer file.',
        delay_note:
          'It may take up to 10 minutes for a customer to see their issued points in the app or web experience.',
        empty_state: {
          note: 'If you are transitioning from a previous loyalty program, this customer’s previous loyalty progress will appear here as transitioned points.',
          description:
            'This customer does not have transferred points on your Thanx loyalty program either because you are not transitioning from a previous loyalty program or because they did not have loyalty progress in the transfer file provided by you to the Thanx team.',
          troubleshoot:
            'If your customer believes that they should have transferred points, check that they are using the email or phone number associated with your previous loyalty program.',
        },
        table: {
          points: 'Points',
          transferred: 'Date points were transferred',
          claimed: 'Date account was claimed',
          status: 'Status of transferred points',
        },
      },
    },
    cards: {
      page_title: 'Cards | Thanx',
      title: 'Cards',
      card_type: 'Card type',
      last4: 'Last four digits',
      date_added: 'Date added',
      loyalty_status: 'Loyalty status',
      digital_ordering_status: 'Digital ordering status',
      empty_state: 'There are no cards for this customer',
      badge: {
        linked: 'Enrolled',
        unlinked: 'Not enrolled',
        archived: 'Archived',
        expired: 'Expired',
        errored: 'Errored',
        payment_linked: 'Enrolled',
        payment_unlinked: 'Not enrolled',
        payment_archived: 'Archived',
        payment_expired: 'Expired',
        payment_errored: 'Errored',
        unavailable: 'Status unavailable',
      },
      loyalty_badge: {
        earning: 'Enrolled',
        linkable: 'Not enrolled',
        failed: 'Enrollment failed',
        other_account: 'Linked to another account',
        ineligible_type: 'Card type not eligible',
        not_required: 'Not required',
      },
    },
    gift_cards: {
      page_title: 'Gift Cards | Thanx',
      title: 'Gift Cards',
      provider: 'Provider',
      last4: 'Last four digits',
      date_added: 'Date added',
      status: 'Status',
      empty_state: 'There are no gift cards for this customer',
      badge: {
        active: 'Active',
        inactive: 'Inactive',
        archived: 'Archived',
      },
    },
    stored_value: {
      page_title: 'Stored Value | Thanx',
      title: 'Stored Value',
    },
    grant_modal: {
      title: 'Grant reward to %{name}',
      points_title: 'Grant points to %{name}',
      subtitle: 'Select reward',
      number_points_to_grant: 'Number of points you want to grant',
      grant_a_reward: 'Grant a reward',
      grant_reward: 'Grant reward',
      grant_points: 'Grant points',
      progress_toward_loyalty: 'Progress toward the loyalty reward',
      delivery_note:
        'Customers will receive this message as a push notification. They will not receive an email.',
      add_message: 'Add a message (optional)',
      how_much_progress: 'How much progress the customer gets',
      alert_success: 'Reward granted successfully',
      alert_success_points: 'Points granted successfully',
      alert_fail: 'Something went wrong',
      points: 'Points',
    },
    actions: {
      edit_tier: 'Edit tier status',
      block_user: 'Block this user',
      grant_purchase: 'Add a purchase',
      remove_points: 'Remove points',
      grant_tier_modal: {
        title: 'Edit tier status',
        subtitle: 'Current tier status',
        upgrade_customer: "Upgrade this customer's tier status",
        select: 'Select tier',
        cancel: 'Cancel',
        submit: 'Upgrade tier status',
        upgrade_to: 'Upgrade to %{tierName}',
        at_gold_tier:
          'This customer is already at the highest tier and cannot be upgraded further',
      },
      confirmation: {
        title: 'Upgrade tier status to %{tierName}',
        message:
          "This customer's tier status will be upgraded from %{oldTier} to %{newTier}. You can't undo this.",
        submit: 'Upgrade',
        alert_success: 'Tier updated successfully.',
        alert_fail: 'Tier was unable to be updated.',
      },
      grant_purchase_modal: {
        title: 'Add a purchase',
        subtitle:
          'If this customer’s purchase wasn’t automatically detected via credit card transaction or via receipt upload, you can add the purchase record manually so this customer gets loyalty progress. You can’t undo this.',
        submit: 'Add this purchase',
        date: 'Date of purchase',
        datePlaceholder: 'MM/DD/YYYY',
        time: 'Time of purchase',
        timePlaceholder: 'Select a time...',
        location: 'Location',
        locationPlaceholder: 'Select a location...',
        locationsLoading: 'Loading locations',
        total: 'Order total',
        dollar: '$',
        days: 'days',
        alert_fail: 'Error creating purchase',
        alert_success: `Success! A purchase record for $%{total} was added.`,
        appear_delay:
          'It may take several minutes for this purchase to appear on the customer’s activity page.',
      },
      remove_points_modal: {
        title: 'Remove points',
        subtitle:
          'Remove points from this customer’s points balance. You can always grant more points later by clicking the “Grant reward” button.',
        amount: 'Number of points you want to remove',
        points: 'points',
        alert_fail: 'There was an error removing this customer’s points.',
        alert_success:
          'Success! %{amount} points have been removed from this customer’s points balance.',
      },
    },
    block_user_modal: {
      title: 'Block user',
      error: 'Error blocking user',
      success: 'User successfully blocked',
      proceed: 'Block',
      description:
        "Revoke this customer's access to your consumer experiences and marketing. They will no longer be able to log in to your app or web experience, earn reward progress, redeem rewards, or make purchases using this email address. You won't be able to undo this.",
    },
  },
  stored_value: storedValue,
}
